import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, type LucideIcon } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Reveal } from "@/hooks/useReveal";

export type ThankYouStep = {
  icon: LucideIcon;
  title: string;
  desc: string;
  when: string;
};

export type ThankYouLink = {
  label: string;
  href: string;
  desc?: string;
};

type Props = {
  metaTitle: string;
  metaDescription: string;
  path: string;
  eyebrow: string;
  heading: string;
  intro: string;
  steps: ThankYouStep[];
  /** Optional confirmation details panel (e.g. server-returned registration record). */
  details?: ReactNode;
  primary: ThankYouLink;
  secondary?: ThankYouLink[];
  footnote?: string;
};

const isExternal = (href: string) => /^https?:\/\//.test(href);

/**
 * Shared success / thank-you screen used after form submissions.
 * Confirms receipt, sets expectations with a timeline, and gives one
 * obvious next step plus a few useful detours.
 */
export function ThankYouPage({
  metaTitle,
  metaDescription,
  path,
  eyebrow,
  heading,
  intro,
  steps,
  details,
  primary,
  secondary = [],
  footnote,
}: Props) {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta title={metaTitle} description={metaDescription} path={path} noindex />
      <SiteNav />

      <main>
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-hero overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 bg-[radial-gradient(circle_at_25%_30%,hsl(var(--gold)/0.12),transparent_55%),radial-gradient(circle_at_80%_70%,hsl(var(--orange)/0.08),transparent_50%)]"
          />
          <div className="container relative max-w-3xl text-center">
            <Reveal variant="up">
              <div className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-gold">
                <CheckCircle2 className="h-3 w-3" /> {eyebrow}
              </div>
              <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-5 leading-[1.02]">
                {heading}
              </h1>
              <p className="text-silver text-base md:text-lg mt-5 leading-relaxed">{intro}</p>
            </Reveal>
          </div>
        </section>

        <section className="py-16 md:py-20">
          <div className="container max-w-3xl">
            {details}
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl">
              What happens next
            </h2>
            <ol className="mt-8 space-y-5">
              {steps.map((step, i) => (
                <li
                  key={step.title}
                  className="flex gap-4 border border-gold/20 bg-black/30 p-5 md:p-6"
                >
                  <div className="shrink-0 flex h-10 w-10 items-center justify-center border border-gold/30 bg-gold/10 text-gold">
                    <step.icon className="h-4 w-4" aria-hidden />
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-display uppercase text-white text-sm tracking-[0.15em]">
                        {i + 1}. {step.title}
                      </h3>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-gold">
                        {step.when}
                      </span>
                    </div>
                    <p className="text-silver text-sm mt-2 leading-relaxed">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10">
              {isExternal(primary.href) ? (
                <a
                  href={primary.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-8 py-4 shadow-cta hover:brightness-110 transition-all"
                >
                  {primary.label} <ArrowRight className="h-3.5 w-3.5" />
                </a>
              ) : (
                <Link
                  to={primary.href}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-8 py-4 shadow-cta hover:brightness-110 transition-all"
                >
                  {primary.label} <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>

            {secondary.length > 0 && (
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {secondary.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="group border border-gold/20 bg-black/20 p-5 hover:border-gold/50 transition-colors"
                  >
                    <span className="font-display uppercase text-white text-sm tracking-[0.15em] group-hover:text-gold transition-colors">
                      {link.label}
                    </span>
                    {link.desc && (
                      <span className="block text-silver text-sm mt-2 leading-relaxed">
                        {link.desc}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}

            {footnote && (
              <p className="mt-10 text-silver text-xs leading-relaxed border-t border-gold/15 pt-6">
                {footnote}
              </p>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
